import {
  addDays,
  CAFFEINE_BEDTIME_HOUR,
  CAFFEINE_BEDTIME_LIMIT_MG,
  CAFFEINE_DAILY_LIMIT_MG,
  CAFFEINE_SINGLE_DOSE_LIMIT_MG,
  caffeineMg,
  caffeineRemainingAt,
  dayEnergy,
  dayNutrientProgress,
  defaultSecondaryTargets,
  deviceTimeZone,
  FIT_MIN_REMAINING_KCAL,
  type FitCandidate,
  isWaterName,
  localDateString,
  localHourOf,
  looksLikeDrink,
  type MealSlot,
  NUTRIENT_DECIMALS,
  phaseFromTarget,
  piecesLabel,
  relativeDayLabel,
  resolveActiveKcal,
  restingEnergy,
  suggestFits,
  vocativeLt,
} from '@gramas/shared'
import {
  Button,
  Card,
  EmptyState,
  IconButton,
  List,
  ListRow,
  MacroBar,
  NutrientGrid,
  NutrientMeter,
  Ring,
  SectionLabel,
} from '@gramas/ui'
import { useLiveQuery } from 'dexie-react-hooks'
import { useEffect, useState } from 'react'
import { db, type LocalLogEntry, type LocalWorkout, refKey } from '../db/dexie.js'
import {
  activeKcalFor,
  copyMeal,
  deleteEntry,
  deleteWorkout,
  entriesForDay,
  fitPool,
  goalForDate,
  latestMeasurements,
  type QuickAddItem,
  restoreEntry,
  restoreWorkout,
  SLOT_LABEL,
  SLOT_ORDER,
  setActiveKcal,
  slotForHour,
  totalsFor,
  unstatedFor,
  workoutsForDay,
} from '../db/repo.js'
import { fireAmbient } from '../glitime/ambient.js'
import { HerZone } from '../glitime/HerZone.js'
import { PlateMotif } from '../glitime/motifs.js'
import { PresenceStrip } from '../glitime/PresenceStrip.js'
import { useZoneState } from '../glitime/zone.js'
import { usePresence } from '../presence/store.js'
import { dailyLine, sassLine } from '../sass/engine.js'
import { sassToast } from '../sass/toast.js'
import { SwipeableRow } from '../shell/SwipeableRow.js'
import { showSassToast } from '../shell/toastStore.js'
import { syncNow } from '../sync/engine.js'
import { OFFER_CARD } from '../wrapped/copy.js'
import { openWrapped, seenWrappedWeek, wrappedOnOffer } from '../wrapped/store.js'

/**
 * What was eaten, said in the unit the source actually published.
 *
 * Most entries are a weight. A few are not: a chain that prices by the portion
 * and states no weight is recorded as portions, and printing the nominal grams
 * that make its arithmetic work would put a number on the row that nobody ever
 * measured.
 */
function amountOf(entry: LocalLogEntry, countedFoodIds: ReadonlySet<string>): string {
  if (entry.servings != null && entry.servings > 0) {
    // Two eggs are two eggs. A chain portion is a portion. Both are counted
    // rather than weighed, and only the food behind the row knows which of the
    // two words is the true one.
    if (entry.foodId != null && countedFoodIds.has(entry.foodId)) {
      return `${piecesLabel(entry.servings)} · ${Math.round(entry.quantityG)} g`
    }
    const portions = Math.round(entry.servings * 10) / 10
    return portions === 1 ? '1 porcija' : `${String(portions).replace('.', ',')} porcijos`
  }
  return `${Math.round(entry.quantityG)} g`
}

import { addWater, GLASS_ML, removeWater } from '../db/repo.js'
import { isSecondaryKey, SECONDARY_LABEL } from './nutrients.js'
import { ActiveBurnSheet } from './sheets/ActiveBurnSheet.js'
import { openBodySheet } from './sheets/bodySheetStore.js'
import { EditEntrySheet } from './sheets/EditEntrySheet.js'
import { type QuickAddPick, QuickAddSheet } from './sheets/QuickAddSheet.js'
import { VoiceLogSheet } from './sheets/VoiceLogSheet.js'
import { WeightSheet } from './sheets/WeightSheet.js'
import { KIND_LABEL, WorkoutSheet } from './sheets/WorkoutSheet.js'
import { useProfile } from './useProfile.js'

/** Which part of the day it is, in the words somebody would actually use. */
function greeting(): string {
  const hour = new Date().getHours()
  if (hour < 11) return 'Labas rytas'
  if (hour < 18) return 'Laba diena'
  return 'Labas vakaras'
}

/** The daily water mark worth one remark. */
const WATER_MILESTONE_ML = 2000

/** How long an empty day sits before her ambient stare fires. */
const AMBIENT_STARE_DELAY_MS = 2 * 60 * 1000

function formatWater(ml: number): string {
  return ml >= 1000 ? `${(ml / 1000).toFixed(1).replace('.', ',')} l` : `${Math.round(ml)} ml`
}

/** Where the day's active burn came from, said plainly under the number. */
const BURN_SUBTITLE: Record<ReturnType<typeof resolveActiveKcal>['origin'], string> = {
  none: 'Įveskite iš laikrodžio arba pridėkite treniruotę',
  manual: 'Įvesta ranka · pridėta prie dienos limito',
  wearable: 'Iš laikrodžio · pridėta prie dienos limito',
  workouts: 'Sudėtos treniruotės · pridėta prie dienos limito',
}

function workoutSubtitle(workout: LocalWorkout): string {
  const parts: string[] = [KIND_LABEL[workout.kind]]
  if (workout.durationMin != null) parts.push(`${Math.round(workout.durationMin)} min`)
  if (workout.distanceKm != null) parts.push(`${workout.distanceKm} km`)
  if (workout.source !== 'manual') parts.push('iš laikrodžio')
  return parts.join(' · ')
}

export function Today() {
  const [date, setDate] = useState(() => localDateString(new Date(), deviceTimeZone()))
  const [burnOpen, setBurnOpen] = useState(false)
  const [weightOpen, setWeightOpen] = useState(false)
  const [workoutSheet, setWorkoutSheet] = useState<{ open: boolean; workout: LocalWorkout | null }>(
    {
      open: false,
      workout: null,
    },
  )
  const [addSlot, setAddSlot] = useState<MealSlot | null>(null)
  /** A suggestion the add sheet should open already holding. */
  const [preselect, setPreselect] = useState<QuickAddPick | null>(null)
  const [voiceOpen, setVoiceOpen] = useState(false)
  const [editEntry, setEditEntry] = useState<LocalLogEntry | null>(null)
  const { profile } = useProfile()
  const zonePhase = useZoneState((state) => state.phase)

  const entries = useLiveQuery(
    async () =>
      (await db.logEntries.where('logDate').equals(date).toArray()).filter(
        (e) => e.deletedAt == null,
      ),
    [date],
    [],
  )
  const goal = useLiveQuery(() => goalForDate(date), [date])
  const summary = useLiveQuery(() => db.wearableDaily.get(date), [date], undefined)
  const workouts = useLiveQuery(() => workoutsForDay(date), [date], [])
  const body = useLiveQuery(() => latestMeasurements(date), [date], {
    weightKg: null,
    bodyFatPct: null,
    waistCm: null,
    muscleKg: null,
    source: undefined,
    weighedOn: null,
  })
  const weightKg = body.weightKg
  const enteredKcal = summary?.activeKcal ?? null

  // Sources are ranked, never summed: a figure copied off the watch already
  // contains the run that was also logged here, and adding them would hand the
  // user a few hundred calories that were never burned.
  const burn = resolveActiveKcal(
    summary?.activeKcal == null
      ? []
      : [{ provider: summary.provider, activeKcal: summary.activeKcal }],
    workouts ?? [],
  )
  const activeKcal = burn.origin === 'none' ? null : burn.activeKcal

  const totals = totalsFor(entries ?? [])
  // The same rows, asked which of the four they refused to answer for.
  const unstated = unstatedFor(entries ?? []).filter(isSecondaryKey)

  /**
   * Catalogue rows behind today's entries that are weighed rather than poured.
   *
   * The caffeine estimate below reads a name and treats the entry's quantity as
   * millilitres, which is right for a drink and nonsense for a slice of
   * "Tiramisu su kava" — 200 g of dessert became 200 ml of coffee. The entry
   * itself does not carry a unit, but the food it was logged from does.
   */
  const weighedFoodIds = useLiveQuery(
    async () => {
      const ids = [...new Set((entries ?? []).flatMap((e) => (e.foodId == null ? [] : [e.foodId])))]
      const foods = await db.foods.bulkGet(ids)
      return new Set(foods.flatMap((f) => (f != null && f.unit !== 'ml' ? [f.id] : [])))
    },
    [entries],
    new Set<string>(),
  )

  /** Foods on today's rows that are counted rather than weighed — see amountOf. */
  const countedFoodIds = useLiveQuery(
    async () => {
      const ids = [...new Set((entries ?? []).flatMap((e) => (e.foodId == null ? [] : [e.foodId])))]
      const foods = await db.foods.bulkGet(ids)
      return new Set(foods.flatMap((f) => (f?.pieceWeightG != null ? [f.id] : [])))
    },
    [entries],
    new Set<string>(),
  )

  // Deletes commit immediately and hand back an undo, instead of asking first.
  const removeEntry = async (entry: LocalLogEntry) => {
    await deleteEntry(entry.id)
    void syncNow()
    sassToast(
      'entry_deleted',
      { itemName: entry.snapshotName },
      `Pašalinta: ${entry.snapshotName}`,
      {
        label: 'Atšaukti',
        run: async () => {
          await restoreEntry(entry.id)
          void syncNow()
        },
      },
    )
  }

  /**
   * How much of each meal there was on the day before this one.
   *
   * Breakfast is the same for months at a time, and rebuilding it item by item
   * every morning is the tax that gets food diaries abandoned in February.
   */
  const yesterdayBySlot = useLiveQuery(
    async () => {
      const previous = await entriesForDay(addDays(date, -1))
      const counts: Record<MealSlot, number> = { breakfast: 0, lunch: 0, dinner: 0, snack: 0 }
      for (const entry of previous) counts[entry.slot]++
      return counts
    },
    [date],
    { breakfast: 0, lunch: 0, dinner: 0, snack: 0 } as Record<MealSlot, number>,
  )

  const yesterdayBurn = useLiveQuery(() => activeKcalFor(addDays(date, -1)), [date], null)

  const repeatYesterday = async (slot: MealSlot) => {
    const copied = await copyMeal({ from: addDays(date, -1), to: date, slot })
    if (copied === 0) return
    void syncNow()
    sassToast('meal_repeated', { slot, count: copied }, `${SLOT_LABEL[slot]}: perkelta ${copied}`)
  }

  // Water speaks only at the two-litre mark: a glass is too small an event
  // for commentary, and the crossing can happen once a day at most.
  const addGlass = async () => {
    const crossed = waterMl < WATER_MILESTONE_ML && waterMl + GLASS_ML >= WATER_MILESTONE_ML
    await addWater(date)
    if (crossed && date === today) {
      const line = sassLine('water_milestone', { waterMl: waterMl + GLASS_ML })
      if (line != null) showSassToast(line)
    }
  }

  const removeWorkout = async (workout: LocalWorkout) => {
    await deleteWorkout(workout.id)
    void syncNow()
    const name = workout.name ?? KIND_LABEL[workout.kind]
    sassToast('workout_deleted', { itemName: name }, `Pašalinta: ${name}`, {
      label: 'Atšaukti',
      run: async () => {
        await restoreWorkout(workout.id)
        void syncNow()
      },
    })
  }

  /**
   * Two numbers nobody has to type.
   *
   * Caffeine is derived rather than stored: no chain prints milligrams, but
   * every drink in the diary already carries a name and a volume, which is all
   * a rough total needs. Water is the one thing here that is genuinely logged,
   * and one tap is the whole interaction it deserves.
   */
  const caffeineDoses = (entries ?? []).flatMap((entry) => {
    if (!looksLikeDrink(entry.snapshotName)) return []
    if (entry.foodId != null && weighedFoodIds.has(entry.foodId)) return []
    const mg = caffeineMg(entry.snapshotName, entry.quantityG)
    if (mg == null || mg <= 0) return []
    // The hour the phone was in when it was logged, so a coffee drunk in
    // Vilnius stays an afternoon coffee after a flight. Unknown hours still
    // count toward the day; they simply cannot say anything about tonight.
    return [{ mg, atHour: localHourOf(entry.loggedAt, entry.loggedTz) }]
  })
  const caffeineMgToday = caffeineDoses.reduce((sum, dose) => sum + dose.mg, 0)
  const strongestDose = caffeineDoses.reduce((most, dose) => Math.max(most, dose.mg), 0)

  /**
   * What will still be circulating at bedtime.
   *
   * The number that decides whether tonight's sleep is any good is not the
   * day's total — it is the half of the four-o'clock latte that has not been
   * cleared by eleven. Caffeine's half-life is about five hours, so the diary
   * already knows this and no other tracker says it.
   */
  const caffeineAtBedtime = caffeineRemainingAt(
    caffeineDoses.flatMap((dose) =>
      dose.atHour == null ? [] : [{ mg: dose.mg, atHour: dose.atHour }],
    ),
    CAFFEINE_BEDTIME_HOUR,
  )
  const waterMl = (entries ?? [])
    .filter((entry) => isWaterName(entry.snapshotName))
    .reduce((sum, entry) => sum + entry.quantityG, 0)
  const kcalTarget = goal?.kcalTarget ?? 2000
  const energy = dayEnergy({ kcalTarget, loggedKcal: totals.kcal, activeKcal })
  const today = localDateString(new Date(), deviceTimeZone())

  // An unset limit is not "no limit": the label on every packet in the shop
  // states a %RI against the EU reference intake, so that is what the app
  // compares against until the user says otherwise.
  const fallback = defaultSecondaryTargets(kcalTarget)
  const secondary = dayNutrientProgress(
    {
      protein: totals.protein,
      carbs: totals.carbs,
      fat: totals.fat,
      fiber: totals.fiber,
      sugars: totals.sugars,
      satFat: totals.satFat,
      salt: totals.salt,
    },
    {
      protein: goal?.proteinG ?? null,
      carbs: goal?.carbsG ?? null,
      fat: goal?.fatG ?? null,
      fiber: goal?.fiberG ?? fallback.fiber,
      sugars: goal?.sugarsG ?? fallback.sugars,
      satFat: goal?.satFatG ?? fallback.satFat,
      salt: goal?.saltG ?? fallback.salt,
    },
  )
  const secondaryShown = secondary.filter((n) => isSecondaryKey(n.key))

  /**
   * Nine in the evening, 350 kcal and 40 g of protein left.
   *
   * The arithmetic is easy and doing it at that hour is not, so what happens
   * instead is a decision on feel and a target missed. Every other tracker
   * leaves this moment alone and offers a search box; this offers three things
   * already in the fridge, because the strongest signal that something is in
   * the fridge is having eaten it before.
   *
   * Held back until the day is more than half eaten — before that everything
   * fits and the card is noise — and dropped entirely once the protein is met.
   */
  const pool = useLiveQuery(() => fitPool(), [], [] as QuickAddItem[])
  const byRefKey = new Map((pool ?? []).map((item) => [refKey(item.kind, item.id), item]))
  const proteinOwed = goal?.proteinG == null ? 0 : goal.proteinG - totals.protein
  const dayBudget = energy.kcalTarget + energy.activeKcal
  const worthSuggesting =
    date === today &&
    proteinOwed > 5 &&
    energy.remainingKcal >= FIT_MIN_REMAINING_KCAL &&
    totals.kcal > dayBudget / 2
  const fits = worthSuggesting
    ? suggestFits({
        candidates: (pool ?? []).map(
          (item): FitCandidate => ({
            id: refKey(item.kind, item.id),
            name: item.name,
            per100g: item.per100g,
            // What one of these normally is. A food with no stated serving is
            // offered as 100 of its unit, which is what the log sheet would
            // have opened on anyway.
            portionG: item.servingSizeG ?? 100,
            unit: item.unit,
          }),
        ),
        remainingKcal: energy.remainingKcal,
        remainingProteinG: proteinOwed,
      })
    : []

  // The voice's once-a-day remarks. Each pick is pinned for its date (the
  // greeting for its daypart), so a re-render refreshes the numbers inside a
  // line without rerolling the line itself. All of these resolve to null the
  // moment the profile says 'neutral', and the neutral JSX takes over.
  const hour = new Date().getHours()
  const resting = restingEnergy(
    {
      weightKg,
      heightCm: profile?.heightCm ?? null,
      birthDate: profile?.birthDate ?? null,
      sex: profile?.sex ?? null,
      activityFactor: profile?.activityFactor ?? null,
    },
    today,
  )
  const phase =
    goal == null || resting.tdee == null
      ? undefined
      : phaseFromTarget(goal.kcalTarget, resting.tdee)
  /**
   * Who she is today, worked out once by the presence controller and read
   * here rather than re-derived.
   *
   * This screen used to compute a mood of its own for the greeting, and it
   * could only ever produce `neglected` or nothing — so the loyal register
   * written for a bad week never appeared on the greeting, and the ring and
   * empty-day surfaces below, which passed no mood at all, could not reach any
   * register at all. The lines existed the whole time.
   */
  const presence = usePresence()

  /**
   * Sunday evening through Monday, once — she offers the week, she does not
   * open it. Tapping is what marks it read, so a card ignored on Sunday is
   * still waiting on Monday morning.
   */
  const wrappedOffer = wrappedOnOffer(new Date())
  const seenWrapped = useLiveQuery(() => seenWrappedWeek(), [])
  const showWrappedCard =
    date === today &&
    wrappedOffer != null &&
    seenWrapped !== undefined &&
    seenWrapped !== wrappedOffer
  const ringLine =
    date !== today || totals.kcal === 0
      ? null
      : energy.isOver
        ? dailyLine('day_over', date, {
            hour,
            overByKcal: -energy.remainingKcal,
            kcalTarget,
            phase,
            proteinOwedG: Math.round(proteinOwed),
            mood: presence.mood,
          })
        : hour >= 18 && energy.remainingKcal <= dayBudget * 0.25
          ? dailyLine('day_under', date, {
              hour,
              remainingKcal: energy.remainingKcal,
              phase,
              mood: presence.mood,
            })
          : null
  const fitsTitle =
    date === today
      ? dailyLine('what_fits', date, {
          hour,
          remainingKcal: energy.remainingKcal,
          proteinOwedG: Math.round(proteinOwed),
        })
      : null
  // The neglect register was written for exactly this screen — twenty-four
  // lines about coming back after a silence, on the one surface that only ever
  // shows during one — and could not be reached until this call started saying
  // how long the silence had run.
  const emptyTitle =
    date === today && (entries ?? []).length === 0
      ? dailyLine('empty_today', date, {
          hour,
          mood: presence.mood,
          daysSinceLastLog: presence.daysSinceLastLog,
        })
      : null

  // Her stare at an empty day, left untouched — the first of the two ambient
  // triggers PERSONA.md's "Netikėtos akimirkos" describes. Armed only while
  // today is genuinely empty and the tab is on screen; a log landing, a swipe
  // to another day, the tab going to the background, or the screen unmounting
  // all clear the timer the same way — by the effect's own condition going
  // false or its cleanup running, never by a flag this effect has to remember
  // itself. `fireAmbient` is the no-op when the day's one slot is already
  // spent, so a re-arm here after coming back from the background costs
  // nothing on a day she has already spoken.
  const isEmptyToday = date === today && (entries ?? []).length === 0
  useEffect(() => {
    if (!isEmptyToday) return
    let timer: ReturnType<typeof setTimeout> | null = null
    const arm = () => {
      if (document.hidden) return
      timer = setTimeout(() => {
        fireAmbient('ambient_stare', new Date().getHours())
      }, AMBIENT_STARE_DELAY_MS)
    }
    const disarm = () => {
      if (timer != null) clearTimeout(timer)
      timer = null
    }
    const onVisibility = () => {
      disarm()
      arm()
    }
    arm()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      disarm()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [isEmptyToday])

  return (
    <div className="h-full overflow-y-auto overscroll-contain">
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-bg/95 px-3 py-2 backdrop-blur-none">
        <IconButton label="Ankstesnė diena" onClick={() => setDate(addDays(date, -1))}>
          <svg viewBox="0 0 20 20" className="size-5" fill="none" aria-hidden="true">
            <path
              d="M12 4l-5 6 5 6"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </IconButton>
        <div className="text-center">
          <div className="font-display text-base font-semibold text-ink">
            {relativeDayLabel(date, today)}
          </div>
          <div className="tnum text-xs text-mute">{date}</div>
        </div>
        <IconButton
          label="Kita diena"
          disabled={date >= today}
          onClick={() => setDate(addDays(date, 1))}
        >
          <svg viewBox="0 0 20 20" className="size-5" fill="none" aria-hidden="true">
            <path
              d="M8 4l5 6-5 6"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </IconButton>
      </header>

      <section className="flex flex-col items-center gap-4 px-4 pb-2 pt-4">
        {/* Only on today. A greeting over a Tuesday three weeks ago would be
            addressing somebody who is not there.

            No longer conditional on a display name: every greeting line in the
            catalogue is written to work without one — a lint enforces it for
            the login screen and the greeting pools ration the vocative to
            under two lines in five — so requiring a name only ever meant a
            nameless account got silence. */}
        {date === today && (
          <div className="w-full">
            <PresenceStrip
              date={date}
              userId={profile?.id ?? 'anon'}
              fallback={
                profile?.displayName != null && profile.displayName.trim() !== ''
                  ? `${greeting()}, ${vocativeLt(profile.displayName)}`
                  : greeting()
              }
            />
          </div>
        )}

        {/* The boot splash measures this ring and lands the mark on it, so the
            launch animation ends by becoming the thing you came here for. */}
        <div data-day-ring data-tour="ring">
          <Ring
            progress={energy.ringProgress}
            value={energy.remainingKcal}
            label={energy.isOver ? 'kcal viršyta' : 'kcal liko'}
            sublabel={`${Math.round(totals.kcal)} / ${energy.kcalTarget + energy.activeKcal} kcal`}
            over={energy.isOver}
          />
        </div>

        {/* One sentence under the ring, only when the day has earned one. The
            ring's own label stays micro-fitted text; this line carries the
            commentary — and, per the voice rules, a number that helps. */}
        {ringLine != null && <p className="tnum w-full text-sm text-mute">{ringLine}</p>}

        {showWrappedCard && wrappedOffer != null && (
          <Card className="w-full p-4">
            <div className="font-display text-base font-semibold text-ink">{OFFER_CARD.title}</div>
            <p className="mt-1 text-sm text-mute">{OFFER_CARD.body}</p>
            <Button className="mt-3" block onClick={() => openWrapped(wrappedOffer)}>
              {OFFER_CARD.action}
            </Button>
          </Card>
        )}

        {/* Two rows, deliberately different weights: three macros at reading
            size, then the four minor nutrients small enough to be scanned in
            one pass and ignored on a day when nothing is red. */}
        {/* Wrapped only so the tour can light both rows at once: its step says
            "three bars, and the four under them", and a highlight around the
            first half of that sentence is a highlight around half a thought. */}
        <div data-tour="macros" className="flex w-full flex-col gap-4">
          <div className="flex w-full gap-3">
            <MacroBar
              size="lg"
              label="Baltymai"
              value={totals.protein}
              target={goal?.proteinG ?? null}
              tone="protein"
            />
            <MacroBar
              size="lg"
              label="Angliavandeniai"
              value={totals.carbs}
              target={goal?.carbsG ?? null}
              tone="carbs"
            />
            <MacroBar
              size="lg"
              label="Riebalai"
              value={totals.fat}
              target={goal?.fatG ?? null}
              tone="fat"
            />
          </div>

          <NutrientGrid columns={4} className="w-full">
            {secondaryShown.map((n) => (
              <NutrientMeter
                key={n.key}
                compact
                label={isSecondaryKey(n.key) ? SECONDARY_LABEL[n.key] : n.key}
                progress={n}
                unit="g"
                // Salt to a tenth, like the packet states it; the rest in whole
                // grams, like the macros above them.
                decimals={NUTRIENT_DECIMALS[n.key]}
              />
            ))}
          </NutrientGrid>
          {/*
            These four are sums of what the labels actually stated, and hardly a
            retailer in the country prints fibre. Saying which ones are short is
            the difference between a number that is a floor and a number that
            pretends to be a total.
          */}
          {unstated.length > 0 && (
            <p className="text-xs text-mute">
              {unstated.map((key) => SECONDARY_LABEL[key]).join(', ')} — sudėta tik ten, kur etiketė
              nurodė. Tikras kiekis didesnis.
            </p>
          )}
        </div>

        <Card data-tour="water" className="w-full">
          <ListRow
            title="Vanduo"
            subtitle="Palieskite +, kad pridėtumėte stiklinę"
            value={waterMl === 0 ? '—' : formatWater(waterMl)}
            accessory={
              <div className="flex items-center gap-1">
                <IconButton
                  label="Atimti stiklinę"
                  disabled={waterMl === 0}
                  onClick={() => void removeWater(date)}
                >
                  <svg viewBox="0 0 20 20" className="size-4" fill="none" aria-hidden="true">
                    <path
                      d="M5 10h10"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                </IconButton>
                <IconButton label="Pridėti stiklinę" onClick={() => void addGlass()}>
                  <svg viewBox="0 0 20 20" className="size-4" fill="none" aria-hidden="true">
                    <path
                      d="M10 5v10M5 10h10"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                </IconButton>
              </div>
            }
          />
          {caffeineMgToday > 0 && (
            <ListRow
              title="Kofeinas"
              wrapSubtitle
              // The most consequential thing it can say. Over the day's limit
              // outranks a single big cup, and both outrank the sleep note —
              // but on an ordinary day the sleep note is the only one that
              // will ever appear, which is why it is here at all. It also runs
              // longest, so this row wraps instead of truncating.
              subtitle={
                caffeineMgToday > CAFFEINE_DAILY_LIMIT_MG
                  ? `Virš EFSA paros ribos (${CAFFEINE_DAILY_LIMIT_MG} mg)`
                  : strongestDose > CAFFEINE_SINGLE_DOSE_LIMIT_MG
                    ? `Vienas gėrimas viršija ${CAFFEINE_SINGLE_DOSE_LIMIT_MG} mg`
                    : caffeineAtBedtime > CAFFEINE_BEDTIME_LIMIT_MG
                      ? `Apie ${caffeineAtBedtime} mg dar liks ${CAFFEINE_BEDTIME_HOUR} val. — miegui daug`
                      : 'Apskaičiuota iš gėrimų'
              }
              value={
                <span
                  className={caffeineMgToday > CAFFEINE_DAILY_LIMIT_MG ? 'text-danger' : undefined}
                >
                  {caffeineMgToday}
                  <span className="text-mute">/{CAFFEINE_DAILY_LIMIT_MG}</span>
                </span>
              }
              meta="mg"
            />
          )}
          <ListRow
            title="Aktyviai sudeginta"
            wrapSubtitle
            subtitle={BURN_SUBTITLE[burn.origin]}
            value={activeKcal == null ? '—' : `${Math.round(activeKcal)} kcal`}
            onActivate={() => setBurnOpen(true)}
          />
          <ListRow
            last
            title="Svoris"
            subtitle={
              weightKg == null
                ? 'Dar nesvertasi'
                : [
                    // Provenance first: the one word that says nobody typed it.
                    body.source === 'scale' ? 'svarstyklės' : null,
                    body.bodyFatPct == null
                      ? null
                      : `riebalai ${body.bodyFatPct.toFixed(1).replace('.', ',')} %`,
                    body.waistCm == null
                      ? null
                      : `juosmuo ${body.waistCm.toFixed(1).replace('.', ',')} cm`,
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'Paskutinis matavimas'
            }
            value={weightKg == null ? '—' : `${weightKg.toFixed(1).replace('.', ',')} kg`}
            // With a body reading on file the row opens the whole panel, which
            // has its own way back to the typing sheet; without one there is
            // nothing to show yet and the sheet is the point.
            onActivate={() => (body.bodyFatPct == null ? setWeightOpen(true) : openBodySheet())}
          />
        </Card>

        {fits.length > 0 && (
          <Card className="w-full">
            <div className="px-4 pb-1 pt-3">
              <p className="text-sm text-ink">{fitsTitle ?? 'Telpa į likutį'}</p>
              <p className="tnum text-xs text-mute">
                Liko {energy.remainingKcal} kcal · baltymų trūksta {Math.round(proteinOwed)} g
              </p>
            </div>
            {fits.map((fit, i) => {
              const item = byRefKey.get(fit.id)
              return (
                <ListRow
                  key={fit.id}
                  last={i === fits.length - 1}
                  title={fit.name}
                  subtitle={`${Math.round(fit.portionG)} ${fit.unit} · B ${fit.proteinG} g`}
                  value={`${fit.kcal}`}
                  meta="kcal"
                  onActivate={() => {
                    if (item == null || item.kind === 'build') return
                    setPreselect({ kind: item.kind, id: item.id, grams: fit.portionG })
                    setAddSlot(slotForHour())
                  }}
                />
              )
            })}
          </Card>
        )}
      </section>

      <section>
        <div className="flex items-baseline justify-between pr-3">
          <SectionLabel>Treniruotės</SectionLabel>
          {burn.workoutKcalIgnored != null && (
            <span className="tnum pt-5 text-xs text-mute">
              įskaičiuota į {burn.origin === 'manual' ? 'įvestą' : 'laikrodžio'} sumą
            </span>
          )}
        </div>
        <List className="border-y border-line bg-surface">
          {(workouts ?? []).map((workout) => (
            <SwipeableRow key={workout.id} onDelete={() => void removeWorkout(workout)}>
              <ListRow
                title={workout.name ?? KIND_LABEL[workout.kind]}
                subtitle={workoutSubtitle(workout)}
                value={workout.kcal == null ? '—' : `${Math.round(workout.kcal)}`}
                meta="kcal"
                onActivate={() => setWorkoutSheet({ open: true, workout })}
              />
            </SwipeableRow>
          ))}
          <button
            type="button"
            onClick={() => setWorkoutSheet({ open: true, workout: null })}
            className="flex h-11 items-center gap-2 px-4 text-left text-base text-accent-text transition-colors hover:bg-raised"
          >
            <svg viewBox="0 0 16 16" className="size-4" fill="none" aria-hidden="true">
              <path
                d="M8 3v10M3 8h10"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
            Pridėti treniruotę
          </button>
        </List>
      </section>

      {SLOT_ORDER.map((slot) => {
        const slotEntries = (entries ?? []).filter((e) => e.slot === slot)
        const slotKcal = slotEntries.reduce((sum, e) => sum + e.kcal, 0)
        // Offered only into an empty meal: with something already in it, one tap
        // would silently double the morning.
        const repeatable = slotEntries.length === 0 && (yesterdayBySlot[slot] ?? 0) > 0
        return (
          // The tour lights the first meal and describes all four; lighting
          // every one of them would be lighting most of the screen.
          <section key={slot} data-tour={slot === SLOT_ORDER[0] ? 'meals' : undefined}>
            <div className="flex items-baseline justify-between pr-3">
              <SectionLabel>{SLOT_LABEL[slot]}</SectionLabel>
              <span className="tnum pt-5 text-xs text-mute">
                {slotEntries.length > 0 ? `${Math.round(slotKcal)} kcal` : ''}
              </span>
            </div>
            <List className="border-y border-line bg-surface">
              {slotEntries.map((entry, i) => (
                <SwipeableRow key={entry.id} onDelete={() => void removeEntry(entry)}>
                  <ListRow
                    last={i === slotEntries.length - 1}
                    title={entry.snapshotName}
                    subtitle={`${amountOf(entry, countedFoodIds)} · B ${Math.round(entry.protein)} · A ${Math.round(entry.carbs)} · R ${Math.round(entry.fat)}`}
                    value={`${Math.round(entry.kcal)}`}
                    meta="kcal"
                    onActivate={() => setEditEntry(entry)}
                  />
                </SwipeableRow>
              ))}
              <button
                type="button"
                onClick={() => setAddSlot(slot)}
                className="flex h-11 items-center gap-2 px-4 text-left text-base text-accent-text transition-colors hover:bg-raised"
              >
                <svg viewBox="0 0 16 16" className="size-4" fill="none" aria-hidden="true">
                  <path
                    d="M8 3v10M3 8h10"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
                Pridėti
              </button>
              {repeatable && (
                <button
                  type="button"
                  onClick={() => void repeatYesterday(slot)}
                  className="flex h-11 items-center gap-2 border-t border-line px-4 text-left text-base text-dim transition-colors hover:bg-raised"
                >
                  <svg viewBox="0 0 16 16" className="size-4" fill="none" aria-hidden="true">
                    <path
                      d="M3 8a5 5 0 105-5V1.5M5.5 3.5L8 1 5.5 -1.5"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Kaip vakar ({yesterdayBySlot[slot]})
                </button>
              )}
            </List>
          </section>
        )
      })}

      {(entries ?? []).length === 0 && (
        <EmptyState
          className="pb-8"
          motif={<PlateMotif />}
          title={emptyTitle ?? 'Dar nieko neužfiksuota'}
          hint="Nuskaitykite brūkšninį kodą arba pridėkite produktą ranka."
          action={
            <Button variant="primary" onClick={() => setAddSlot(slotForHour())}>
              Pridėti produktą
            </Button>
          }
        />
      )}

      <div className="h-6" />

      <ActiveBurnSheet
        open={burnOpen}
        onOpenChange={setBurnOpen}
        date={date}
        current={enteredKcal}
        previous={yesterdayBurn}
        onSave={async (value) => {
          await setActiveKcal(date, value)
          void syncNow()
        }}
      />

      {/* Prefilled from the newest reading of each field separately, so a daily
          weigh-in never blanks a waist measured last week. */}
      <WeightSheet open={weightOpen} date={date} current={body} onOpenChange={setWeightOpen} />

      <WorkoutSheet
        open={workoutSheet.open}
        date={date}
        weightKg={weightKg}
        existing={workoutSheet.workout}
        onOpenChange={(open) => setWorkoutSheet((s) => ({ ...s, open }))}
      />

      <EditEntrySheet entry={editEntry} onOpenChange={(open) => !open && setEditEntry(null)} />

      <QuickAddSheet
        open={addSlot != null}
        slot={addSlot ?? 'snack'}
        date={date}
        preselect={preselect}
        onOpenChange={(open) => {
          if (open) return
          setAddSlot(null)
          setPreselect(null)
        }}
        onVoice={() => {
          setAddSlot(null)
          setPreselect(null)
          setVoiceOpen(true)
        }}
      />

      <VoiceLogSheet open={voiceOpen} date={date} onOpenChange={setVoiceOpen} />

      {zonePhase !== 'closed' && <HerZone />}
    </div>
  )
}
